import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";

// A static visual preview of the app's monthly budget calendar.
// Hardcoded so visitors can see what they're getting before they sign up.

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const TODAY = 15;

// Pre-built bill/expense pills for select days in a fictional month (Nov, 1st = Sat)
const DATA = {
  1:  [{ type: "bill",    name: "Rent",        amt: 1850, paid: true }],
  3:  [{ type: "expense", name: "Groceries",   amt: 86 }],
  5:  [{ type: "bill",    name: "Internet",    amt: 60 }],
  8:  [{ type: "expense", name: "Coffee",      amt: 12 }],
  10: [{ type: "bill",    name: "Electric",    amt: 112, paid: true }],
  12: [{ type: "expense", name: "Dinner out",  amt: 48 }],
  15: [{ type: "bill",    name: "Credit card", amt: 340 }],
  18: [{ type: "expense", name: "Gas",         amt: 52 }],
  20: [{ type: "bill",    name: "Phone",       amt: 45 }],
  22: [{ type: "expense", name: "Groceries",   amt: 94 }],
  25: [{ type: "bill",    name: "Gym",         amt: 29 }],
  28: [{ type: "bill",    name: "Streaming",   amt: 22, paid: true }],
};

export default function CalendarPreview() {
  // Nov 2025: starts on Saturday (startWeekday = 6), 30 days
  const startWeekday = 6;
  const daysInMonth = 30;
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push({ inactive: true, key: `p-${i}` });
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, items: DATA[d] || [], isToday: d === TODAY, key: d });
  }

  return (
    <section id="calendar" className="bg-[#F2EFE7] py-20 border-y border-[#E6E3DB]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-4">
            <p className="eyebrow text-[#C05C3D]" data-testid="calendar-preview-eyebrow">The Calendar</p>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 leading-tight">
              Your month, <br /> on one page.
            </h2>
            <p className="mt-6 text-[#4d4f4c] leading-relaxed">
              Place bills on the days they're due. Log expenses as they happen. Tick things off as
              they're paid. One quiet, honest view of your money.
            </p>
            <ul className="mt-8 space-y-2 text-sm text-[#4d4f4c]">
              <li className="flex items-center gap-2">
                <span className="inline-block w-3 h-3 bg-[#FFF6F1] border-l-2 border-[#C05C3D]" /> Bill due
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-block w-3 h-3 bg-[#F3F6F3] border-l-2 border-[#607762]" /> Expense / paid bill
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-block w-3 h-3 ring-2 ring-[#C05C3D] bg-white" /> Today
              </li>
            </ul>
            <Link
              to="/register"
              className="mt-10 inline-flex items-center gap-2 px-6 py-3 rounded-sm bg-[#2C302E] text-white hover:bg-[#1b1e1c] transition-colors text-sm"
              data-testid="calendar-preview-cta"
            >
              Open my calendar <ExternalLink className="h-3 w-3" />
            </Link>
          </div>

          <div className="lg:col-span-8">
            <div className="bg-white border border-[#E6E3DB] p-4 md:p-6 shadow-[0_1px_0_0_rgba(44,48,46,0.04)]">
              <div className="flex items-center justify-between mb-4">
                <p className="font-serif text-2xl">November 2025</p>
                <div className="text-xs text-[#787671] tracking-widest uppercase hidden sm:block">
                  preview
                </div>
              </div>

              <div className="grid grid-cols-7 mb-1 text-[10px] tracking-widest uppercase text-[#787671]">
                {WEEKDAYS.map((d) => (
                  <div key={d} className="px-1 py-1">{d}</div>
                ))}
              </div>

              <div className="grid grid-cols-7 border border-[#E6E3DB] bg-[#E6E3DB] gap-px">
                {cells.map((c) => (
                  <div
                    key={c.key}
                    className={`bg-white min-h-[84px] md:min-h-[100px] p-2 ${
                      c.inactive ? "bg-[#F7F5F0] opacity-50" : ""
                    } ${c.isToday ? "ring-2 ring-inset ring-[#C05C3D] bg-[#FFF8F4]" : ""}`}
                  >
                    {!c.inactive && (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="font-serif text-sm md:text-base">{c.day}</span>
                        </div>
                        <div className="mt-1 space-y-1">
                          {c.items.slice(0, 2).map((it, i) => (
                            <div
                              key={`${c.day}-${it.name}-${i}`}
                              className={`text-[9px] md:text-[10px] px-1 py-0.5 border-l-2 truncate ${
                                it.type === "bill" && !it.paid
                                  ? "border-[#C05C3D] bg-[#FFF6F1]"
                                  : "border-[#607762] bg-[#F3F6F3] text-[#607762]"
                              } ${it.paid ? "line-through text-[#787671]" : ""}`}
                            >
                              {it.name} · ${it.amt}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
